import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Modal, Textinput, Flex, Typo, theme } from '../../../ui';
import { CALENDAR_EVENT_COLORS } from '../data/classroomConstants';

const NativeInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${theme.colors.NEU_200};
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  color: ${theme.colors.NEU_PRIMARY};
  background: ${theme.colors.NEU_0};

  &:focus {
    outline: none;
    border-color: ${theme.colors.PRIMARY};
  }
`;

const NativeSelect = styled.select`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${theme.colors.NEU_200};
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  color: ${theme.colors.NEU_PRIMARY};
  background: ${theme.colors.NEU_0};

  &:focus {
    outline: none;
    border-color: ${theme.colors.PRIMARY};
  }
`;

const FieldLabel = styled(Typo.SmallText)`
  color: ${theme.colors.NEU_600};
  font-weight: 500;
  margin-bottom: 4px;
`;

const AddEventModal = ({ open, onClose, onSubmit, prefilledDate, sections }) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [eventType, setEventType] = useState('live_session');
  const [date, setDate] = useState(prefilledDate || '');
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('10:30');
  const [sectionName, setSectionName] = useState('');

  const handleAccept = () => {
    const startDate = new Date(`${date}T${startTime}`);
    const endDate = new Date(`${date}T${endTime}`);
    const isAllDay = eventType === 'assignment_due';

    const newEvent = {
      id: `local-event-${Date.now()}`,
      title,
      start: startDate.toISOString(),
      end: endDate.toISOString(),
      allDay: isAllDay,
      backgroundColor: CALENDAR_EVENT_COLORS[eventType] || CALENDAR_EVENT_COLORS.custom,
      borderColor: CALENDAR_EVENT_COLORS[eventType] || CALENDAR_EVENT_COLORS.custom,
      extendedProps: {
        type: eventType,
        sectionName,
      },
    };

    onSubmit(newEvent);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('classroom.addEvent', 'Add Calendar Event')}
      width='480px'
      showActions
      onAccept={handleAccept}
      acceptLabel={t('classroom.addEventButton', 'Add Event')}
      acceptIsDisabled={!title.trim() || !date}
    >
      <Flex direction='column' gap='16'>
        <Textinput
          label={t('classroom.eventTitle', 'Title')}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder='e.g. Mathematics - Live Lesson'
        />

        <Flex direction='column' gap='4'>
          <FieldLabel>{t('classroom.eventType', 'Type')}</FieldLabel>
          <NativeSelect value={eventType} onChange={(e) => setEventType(e.target.value)}>
            <option value='live_session'>{t('classroom.liveSession', 'Live Session')}</option>
            <option value='assignment_due'>{t('classroom.assignmentDue', 'Assignment Due')}</option>
            <option value='exam'>{t('classroom.exam', 'Exam')}</option>
            <option value='custom'>{t('classroom.custom', 'Custom')}</option>
          </NativeSelect>
        </Flex>

        <Flex direction='column' gap='4'>
          <FieldLabel>{t('classroom.date', 'Date')}</FieldLabel>
          <NativeInput type='date' value={date} onChange={(e) => setDate(e.target.value)} />
        </Flex>

        <Flex gap='12'>
          <Flex direction='column' gap='4' style={{ flex: 1 }}>
            <FieldLabel>{t('classroom.startTime', 'Start Time')}</FieldLabel>
            <NativeInput type='time' value={startTime} onChange={(e) => setStartTime(e.target.value)} />
          </Flex>
          <Flex direction='column' gap='4' style={{ flex: 1 }}>
            <FieldLabel>{t('classroom.endTime', 'End Time')}</FieldLabel>
            <NativeInput type='time' value={endTime} onChange={(e) => setEndTime(e.target.value)} />
          </Flex>
        </Flex>

        <Textinput
          label={t('classroom.sectionUnit', 'Section / Unit')}
          value={sectionName}
          onChange={(e) => setSectionName(e.target.value)}
          placeholder='e.g. Mathematics'
        />
      </Flex>
    </Modal>
  );
};

export default AddEventModal;
